import type { ApiErrorBody } from "@caloteiros/shared";

/** Em desenvolvimento o Vite faz proxy de /api para a API: mesma origem, zero CORS. */
const BASE_URL = "/api/v1";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string> | undefined;

  constructor(status: number, code: string, message: string, fields?: Record<string, string>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

/**
 * O access token vive so na memoria. O que sobrevive ao reload e o refresh
 * token, que fica num cookie httpOnly — inalcancavel por javascript, inclusive
 * pelo de um eventual XSS.
 */
let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function onSessionExpired(handler: () => void): void {
  onSessionLost = handler;
}

/** Uma renovacao por vez: dez requests em paralelo nao viram dez refreshes. */
let refreshInFlight: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (!response.ok) return false;
    const data = (await response.json()) as { accessToken: string };
    accessToken = data.accessToken;
    return true;
  } catch {
    return false;
  } finally {
    // Libera a proxima tentativa so depois desta terminar.
    refreshInFlight = null;
  }
}

function refreshAccessToken(): Promise<boolean> {
  return refreshInFlight ?? (refreshInFlight = doRefresh());
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Rotas publicas (o link compartilhado) nao mandam token nem tentam renovar. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, anonymous = false, signal } = options;

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (accessToken && !anonymous) headers.Authorization = `Bearer ${accessToken}`;

    return fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      credentials: "include",
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  };

  let response = await send();

  // Token vencido: renova uma vez e repete a chamada, sem o usuario perceber.
  if (response.status === 401 && !anonymous) {
    const renewed = await refreshAccessToken();
    if (renewed) {
      response = await send();
    } else {
      accessToken = null;
      onSessionLost?.();
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? "UNKNOWN",
      error?.message ?? "Não consegui falar com o servidor.",
      error?.fields,
    );
  }

  return payload as T;
}
